import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { getPortfolioData, saveSectionData } from "./services/firestoreService";
import { defaultPortfolioData } from "./services/seedData";

const PortfolioDataContext = createContext(null);

export const PortfolioDataProvider = ({ children }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    return getPortfolioData()
      .then((d) => {
        if (!d || typeof d !== "object" || Object.keys(d).length === 0) {
          // Firestore returned nothing or an empty document — use seed defaults
          setData(defaultPortfolioData);
          return;
        }
        // Deep-merge: Firestore data wins, missing keys fall back to defaults
        // Migrate old skills shape { coding, interpersonal } → { categories }
        let mergedSkills;
        // If the new categories array exists at all (even empty), the user has
        // explicitly managed skills in the admin — always respect it.
        if (Array.isArray(d.skills?.categories)) {
          mergedSkills = { categories: d.skills.categories };
        } else if (d.skills?.coding?.length || d.skills?.interpersonal?.length) {
          // One-time migration: convert legacy percentage-based arrays to categories
          const cats = [];
          if (d.skills.coding?.length) {
            cats.push({ category: "Technical Skills", items: d.skills.coding.map((s) => s.name) });
          }
          if (d.skills.interpersonal?.length) {
            cats.push({ category: "Interpersonal", items: d.skills.interpersonal.map((s) => s.name) });
          }
          mergedSkills = { categories: cats };
        } else {
          // No valid skills in Firestore — use seed defaults and persist them
          // so this auto-heal only runs once.
          mergedSkills = defaultPortfolioData.skills;
          saveSectionData("skills", { skills: defaultPortfolioData.skills }).catch(() => { });
        }
        setData({
          ...defaultPortfolioData,
          ...d,
          profile: { ...defaultPortfolioData.profile, ...(d.profile || {}) },
          skills: mergedSkills,
          certifications: d.certifications ?? defaultPortfolioData.certifications,
          awards: d.awards ?? defaultPortfolioData.awards,
          languages: d.languages ?? defaultPortfolioData.languages,
          companies: d.companies ?? defaultPortfolioData.companies,
        });
      })
      .catch((err) => {
        console.warn("Firestore unavailable, using seed data:", err.message);
        setData(defaultPortfolioData);
        setError(err);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <PortfolioDataContext.Provider value={{ data, loading, error, refresh: fetchData }}>
      {children}
    </PortfolioDataContext.Provider>
  );
};

export const usePortfolioData = () => useContext(PortfolioDataContext);
